<template>
  <section class="skin-stat">
    <p class="skin-title">{{ t('Price Statistics') }}</p>
    <div class="skin-stat_wrap">
      <div v-if="Object.keys(item.price_statistics).some(i => !!item.price_statistics?.[i])"
           class="card-dark">
        <p class="gray mb12">{{ t('Last') }} 7 {{ t('days') }}</p>
        <div class="skin-stat_graph">
          <div
              v-for="day in priceStatisticsWithPadding"
              :key="day?.date + '-price'"
              v-tooltip="day?.date"
              :class="{
                high: day?.min_price === weekAvg.maxAvg && !!day?.min_price && !!weekAvg?.maxAvg,
                low: day?.min_price === weekAvg.minAvg
              }"
              :style="{ height: `${100 / (weekAvg.maxAvg / (day?.min_price || 0))}%` }"
              class="skin-stat_graph-item"
          >
            <UiPrice
                v-tooltip="day?.date"
                :price="day?.min_price" />
          </div>
        </div>
      </div>
      <div v-if="item.price_statistics"
           class="skin-stat_rows g g1 gap16 mt16">
        <div class="f">
          <p class="title">{{ t('Current Price') }}</p>
          <div class="f">
            <UiPrice :price="item.current_price" />
          </div>
        </div>
        <div class="f">
          <p class="title">7 {{ t('Days Price Change') }}</p>
          <div class="f">
            <i
                v-if="sevenDaysPercentage"
                :class="`i-arrow-long mr4 ${
                sevenDaysPercentage > 0 ? 'green rot_180' : 'red'
              }`"
            ></i>
            <div
                v-if="item.price_statistics.seven_day_average"
                class="f mx4"
            >
              {{ sevenDaysPercentage > 0 ? '+' : '-' }}
              <UiPrice
                  :price="Math.abs(item.current_price - item.price_statistics.seven_day_average).toFixed(2)" />
            </div>
            <span v-else> N/A </span>
            <b>({{ sevenDaysPercentage.toFixed(2) }}%)</b>
          </div>
        </div>
        <div class="f">
          <p class="title">7 {{ t('Days Low') }}</p>
          <div class="f">
            <UiPrice :price="item.price_statistics.seven_day_low" />
          </div>
        </div>
        <div class="f">
          <p class="title">7 {{ t('Days High') }}</p>
          <div class="f">
            <UiPrice :price="item.price_statistics.seven_day_high" />
          </div>
        </div>
        <div class="f">
          <p class="title">30 {{ t('Days Low') }}</p>
          <div class="f">
            <UiPrice :price="item.price_statistics.thirty_day_low" />
          </div>
        </div>
        <div class="f">
          <p class="title">30 {{ t('Days High') }}</p>
          <div class="f">
            <UiPrice :price="item.price_statistics.thirty_day_high" />
          </div>
        </div>
        <div class="f">
          <p class="title">90 {{ t('Days Low') }}</p>
          <div class="f">
            <UiPrice :price="item.price_statistics.ninety_day_low" />
          </div>
        </div>
        <div class="f">
          <p class="title">90 {{ t('Days High') }}</p>
          <div class="f">
            <UiPrice :price="item.price_statistics.ninety_day_high" />
          </div>
        </div>
        <div class="f">
          <p class="title">{{ t('All time low') }}</p>
          <div class="f">
            <UiPrice :price="item.price_statistics.all_time_low" />
          </div>
        </div>
        <div class="f">
          <p class="title">{{ t('All time high') }}</p>
          <div class="f">
            <UiPrice :price="item.price_statistics.all_time_high" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import {useI18n} from 'vue-i18n'
import type {ItemPageInfo} from "~/graphql/generated/schema-types";

const {t} = useI18n()

const props = defineProps<{
  item: ItemPageInfo
}>()

const priceStatisticsWithPadding = computed(() => {
  let chart = props.item.price_statistics?.seven_day_chart || [];

  // Собираем уже существующие даты в Set для быстрой проверки уникальности
  const existingDates = new Set(chart.map(entry => entry?.date));

  // Добавляем недостающие дни до 7
  const padding = [];
  for (let i = 0; padding.length < 7 - chart.length; i++) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;

    // Проверяем, если такая дата уже есть в массиве, не добавляем ее
    if (!existingDates.has(formattedDate)) {
      padding.push({
        avg_price: 0,
        date: formattedDate,
        min_price: 0,
        max_price: 0,
        total_count: '0',
        total_value: '0'
      });
      existingDates.add(formattedDate); // Добавляем дату в Set, чтобы избежать добавления повторений
    }
  }

  chart = [...padding, ...chart];

  // Сортируем по дате
  chart.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return chart;
});

const weekAvg = computed(() => {
  let minAvg = 0;
  let maxAvg = 0;
  priceStatisticsWithPadding.value.forEach((i, index) => {
    if (i) {
      if (index === 0) {
        minAvg = i.min_price;
      }
      if (i.min_price < minAvg) minAvg = i.min_price;
      if (i.min_price > maxAvg) maxAvg = i.min_price;
    }
  })
  return {minAvg, maxAvg}
})

// const week = ref<number | null>(null)
// const weekLow = ref<number | null>(null)
// const weekHigh = ref<number | null>(null)
//
// const sortedWeekDays = (props.item.seven_days || [])
//     .filter((item: any) => item.avg_price)
//     .sort((a: any, b: any) => +a.avg_price - +b.avg_price)
//
// weekLow.value = +(sortedWeekDays[0]?.avg_price || 0);
// weekHigh.value = +(sortedWeekDays[sortedWeekDays.length - 1]?.avg_price || 0)


const sevenDaysPercentage = computed(() => {
      if (props.item.price_statistics?.seven_day_average && props.item?.current_price) {
        return ((props.item?.current_price - props.item.price_statistics?.seven_day_average) / props.item.price_statistics?.seven_day_average) * 100
      }
      return 0
    }
)

// function getPriceStatsForPeriod(data: WeeklyChart[], periodLength: number) {
//   if (!data.length) return {minPrice: 0, maxPrice: 0};
//
//   const periods = Math.ceil(data.length / periodLength);
//   let results = {} as { minPrice: number; maxPrice: number };
//
//   for (let i = 0; i < periods; i++) {
//     const periodData = data.slice(i * periodLength, (i + 1) * periodLength);
//     let minPrice = Number.MAX_SAFE_INTEGER;
//     let maxPrice = Number.MIN_SAFE_INTEGER;
//
//     periodData.forEach(item => {
//       const min = parseInt(item.min_price?.toString(), 10);
//       const max = parseInt(item.max_price?.toString(), 10);
//       if (min < minPrice) minPrice = min;
//       if (max > maxPrice) maxPrice = max;
//     });
//
//     results = {minPrice, maxPrice}
//   }
//
//   return results;
// }


// const month = ref(getPriceStatsForPeriod((props.item.chart || []) as WeeklyChart[], 4))
// const threeMonths = ref(getPriceStatsForPeriod((props.item.chart || []) as WeeklyChart[], 12))
// const allTime = ref(getPriceStatsForPeriod((props.item.chart || []) as WeeklyChart[], props.item.chart?.length || 0))
//
//
// const weekPrice = ref<string | number>(0)
// const weekPercentage = ref<string | number>(0)

// function calculatePriceChange(prices: number[], currentPrice: number) {
//   // Суммируем все цены за последние 7 дней
//   let total = prices.reduce((sum, price) => sum + price, 0);
//   // Вычисляем среднюю цену за 7 дней
//   let averagePrice = total / prices.length;
//   // Рассчитываем абсолютное изменение цены
//   let priceChange = currentPrice - averagePrice;
//   // Рассчитываем процентное изменение цены
//   let percentageChange = (priceChange / averagePrice) * 100;
//   // Возвращаем результаты в виде объекта
//   return {
//     priceChange: priceChange.toFixed(2),
//     percentageChange: percentageChange.toFixed(2),
//   };
// }
//
// let lastWeekPrices: number[] = props.item.seven_days?.map(item => item?.min_price || 0) || []; // Цены за последние 7 дней
// let todayPrice = props.item.current_price || 0; // Цена сегодня
//
// const {priceChange, percentageChange} = calculatePriceChange(lastWeekPrices, todayPrice);
// weekPrice.value = priceChange
// weekPercentage.value = percentageChange

</script>

<style lang="scss" scoped>

@use 'sass:color';

.skin-stat {
  .card-dark {
    padding: 1.6rem;
  }

  &_graph {
    height: 17.7rem;
    display: flex;
    gap: 0.8rem;
    justify-content: space-between;
    align-items: flex-end;

    &-item {
      max-width: 35px;
    }

    &:deep(.tooltip .tooltip-text) {
      min-width: 95px;
      writing-mode: initial;
      transform: rotate(180deg) translateX(50%);
      top: 105%;

      &::after {
        top: 100%;
        left: 50%;
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: rgba(87, 229, 208, 0.7) transparent transparent transparent;
      }
    }

    div {
      writing-mode: vertical-rl;
      text-orientation: unset;
      transform: rotate(180deg);
      background: rgba(255, 255, 255, 0.1);
      padding: 1.6rem 0.2rem;
      border-radius: 10rem;
      color: var(--surfaces-on-surface-variable);
      font-weight: 600;
      flex-grow: 1;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      min-height: fit-content;

      &.low {
        color: var(--destructive);
        background: rgba(255, 87, 87, 0.3);
      }

      &.high {
        color: var(--accent-on-primary);
        background: var(--accent-secondary);
      }
    }
  }

  &_rows {
    font-size: 1.6rem;
    font-weight: 800;
    color: var(--surfaces-on-surface);

    .title {
      color: var(--gray);
      font-weight: 400;
      font-size: 1.4rem;
    }

    b {
      font-weight: 400;
      font-size: 1.4rem;
    }
  }
}
</style>

<style lang="scss" scoped>


.skin-stat_graph .price {
  background: none;
  transform: none;
  padding: 0;
  color: var(--surfaces-on-surface-variable);
}

.skin-stat_graph .low .price {
  color: var(--destructive);
}

.skin-stat_graph .high .price {
  //color: var(--accent-on-primary);
  color: var(--accent-on-accent-secondary);
}
</style>
